import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSharedState } from "sharedStateProvider";
import type { CallFeedbackDetail } from "types/call-feedback-type";
import NavbarContainer from "components/navbar";
import { getFeedbackById } from "api/call-feedback";
import { getApiErrorMessage } from "api/error";
import { IconArrowLeft } from "@tabler/icons-react";

/** Display datetime exactly as returned by API (no timezone conversion) */
function formatDateTimeRaw(isoString: string): string {
  return isoString.trim() || "—";
}

function DetailRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="row mb-2">
      <div className="col-3 text-muted">{label}</div>
      <div className="col-9">{value}</div>
    </div>
  );
}

export default function FeedbackDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { notify } = useSharedState();

  const [feedback, setFeedback] = useState<CallFeedbackDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchFeedback = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const data = await getFeedbackById(id);
      setFeedback(data);
    } catch (error: unknown) {
      notify(
        getApiErrorMessage(error, "Ошибка при загрузке отзыва"),
        "danger",
        "Ошибка загрузки"
      );
      navigate("/call-feedback", { replace: true });
    } finally {
      setIsLoading(false);
    }
  }, [id, navigate, notify]);

  useEffect(() => {
    fetchFeedback();
  }, [fetchFeedback]);

  if (isLoading || !feedback) {
    return (
      <div className="page">
        <NavbarContainer />
        <div className="page-wrapper">
          <div className="page-body">
            <div className="container-xl">
              <div className="text-muted">Загрузка…</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <NavbarContainer />
      <div className="page-wrapper">
        <div className="page-header d-print-none">
          <div className="container-xl">
            <div className="row align-items-center">
              <div className="col">
                <button
                  type="button"
                  className="btn btn-ghost-secondary btn-icon mb-1"
                  onClick={() => navigate("/call-feedback")}
                  aria-label="Назад к списку"
                >
                  <IconArrowLeft className="icon" />
                </button>
                <h2 className="page-title">Отзыв о звонке</h2>
                <div className="text-muted">ID: {feedback.id}</div>
              </div>
            </div>
          </div>
        </div>
        <div className="page-body">
          <div className="container-xl">
            <div className="row">
              <div className="col-12">
                <div className="card mb-3">
                  <div className="card-header">
                    <h3 className="card-title">Основные данные</h3>
                  </div>
                  <div className="card-body">
                    <DetailRow label="ID" value={feedback.id} />
                    <DetailRow
                      label="Пользователь"
                      value={
                        <a
                          href={`/user/${feedback.user_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {feedback.user_id}
                        </a>
                      }
                    />
                    <DetailRow
                      label="Сессия звонка"
                      value={feedback.call_session_id || "—"}
                    />
                    <DetailRow label="Оценка" value={String(feedback.rating)} />
                    <DetailRow
                      label="Описание"
                      value={
                        feedback.description ? (
                          <pre
                            className="mb-0 text-break p-3 rounded"
                            style={{
                              whiteSpace: "pre-wrap",
                              backgroundColor: "var(--tblr-bg-surface-secondary, #f1f3f5)",
                              color: "var(--tblr-body-color, #212529)",
                            }}
                          >
                            {feedback.description}
                          </pre>
                        ) : (
                          "—"
                        )
                      }
                    />
                    <DetailRow
                      label="Создано"
                      value={formatDateTimeRaw(feedback.created_at)}
                    />
                    <DetailRow
                      label="Обновлено"
                      value={formatDateTimeRaw(feedback.updated_at)}
                    />
                  </div>
                </div>
                {feedback.metadata != null && Object.keys(feedback.metadata).length > 0 && (
                  <div className="card">
                    <div className="card-header">
                      <h3 className="card-title">Метаданные</h3>
                    </div>
                    <div className="card-body">
                      <pre
                        className="mb-0 p-3 rounded"
                        style={{
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          maxHeight: 480,
                          overflow: "auto",
                          backgroundColor: "var(--tblr-bg-surface-secondary, #f1f3f5)",
                          color: "var(--tblr-body-color, #212529)",
                        }}
                      >
                        {JSON.stringify(feedback.metadata, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
